import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Upload, Trash, MagnifyingGlass, DownloadSimple, Table, Scales, CheckSquare, Square, Tray, CheckCircle, XCircle } from '@phosphor-icons/react';
import { ViewToggle, useViewMode } from '../components/ViewToggle';
import { PowerTable } from '../components/PowerTable';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useNavigate, Link } from 'react-router-dom';
import BulkUploadModal from '../components/BulkUploadModal';
import useAuthStore from '../store/authStore';
import { confirm } from '../components/ConfirmDialog';
import useBulkSelection from '../hooks/useBulkSelection';
import BulkDeleteDialog, { BulkActionBar } from '../components/BulkActions';
import { fileUrl } from '../lib/fileUrl';
import { ScGatedButton } from '../components/ScGatedButton';

import { notify } from '../lib/notify';
import { InlineSkeleton, PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'ICU Care',
  'Emergency Care',
  'Ward Care',
  'Transport Care',
  'Accessories',
  'Examination Care',
  'Office Solutions',
  'Seating Solutions',
  'Consultation Solutions',
  'Cafeteria Solutions',
  'Waste Management'
];

// HSN Code to GST Rate mapping for medical equipment
// Supports 4-digit, 6-digit, and 8-digit HSN codes
const HSN_TAX_MAPPING = {
  // 8-digit specific codes
  '87139010': 5,   // Parts and accessories of wheelchairs and invalid carriages
  '90181100': 12,  // Electro-cardiographs
  '90181900': 12,  // Other electro-diagnostic apparatus
  '90189010': 12,  // Patient monitors
  '90189020': 12,  // Defibrillators
  '90192000': 12,  // Ozone, oxygen, aerosol therapy apparatus
  '90211000': 5,   // Orthopaedic or fracture appliances
  '90213100': 5,   // Artificial joints
  '90213900': 5,   // Other artificial parts of the body
  '94029000': 18,  // Other medical furniture
  
  // 6-digit codes
  '901811': 12,  // Electro-cardiographs
  '901819': 12,  // Other electro-diagnostic apparatus
  '901890': 12,  // Other instruments and appliances
  '902110': 5,   // Orthopaedic or fracture appliances
  '902131': 5,   // Artificial joints
  '940290': 18,  // Other medical furniture
  
  // 4-digit codes (broader categories)
  '8713': 5,   // Wheelchairs and invalid carriages
  '9018': 12,  // Medical, surgical, dental or veterinary instruments
  '9019': 12,  // Mechano-therapy appliances; massage apparatus
  '9020': 12,  // Breathing appliances and gas masks
  '9021': 5,   // Orthopaedic appliances
  '9022': 12,  // Apparatus based on X-rays or alpha, beta or gamma radiations
  '9402': 18,  // Medical, surgical, dental or veterinary furniture
  '9404': 12,  // Mattress supports; articles of bedding
  '7326': 18,  // Other articles of iron or steel
  '3926': 18,  // Other articles of plastics
  '4818': 12,  // Paper, cellulose wadding or fibres (disposables)
};

const getTaxBracket = (hsnCode) => {
  if (!hsnCode) return 0;
  
  // Remove any spaces or special characters
  const cleanHsn = hsnCode.replace(/[^0-9]/g, '');
  
  // Try exact match first (8-digit)
  if (HSN_TAX_MAPPING[cleanHsn]) {
    return HSN_TAX_MAPPING[cleanHsn];
  }
  
  // Try 6-digit prefix
  if (cleanHsn.length >= 6) {
    const hsn6 = cleanHsn.substring(0, 6);
    if (HSN_TAX_MAPPING[hsn6]) {
      return HSN_TAX_MAPPING[hsn6];
    }
  }
  
  // Try 4-digit prefix
  if (cleanHsn.length >= 4) {
    const hsn4 = cleanHsn.substring(0, 4);
    if (HSN_TAX_MAPPING[hsn4]) {
      return HSN_TAX_MAPPING[hsn4];
    }
  }
  
  // Default to 18% if no match found
  return 18;
};

const Products = () => {
  const { user } = useAuthStore();
  // SC + flag-aware access. These derived values show the appropriate
  // control (real button for VP/President; a disabled gated button for
  // Sales Coordinator when the President has flipped the relevant flag).
  // Role-based access, widened by President / Super Admin "Product Upload Access" grants.
  const [uploadAccess, setUploadAccess] = useState({ can_create: false, can_bulk_import: false });
  useEffect(() => {
    axios.get(`${API}/product-upload-access/me`).then((r) => setUploadAccess(r.data)).catch(() => {});
  }, []);
  const isVpPlus = ['Vice President', 'President'].includes(user?.role);
  const canBulkUpload = isVpPlus || uploadAccess.can_bulk_import;
  const canCreateProduct = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role) || uploadAccess.can_create;
  const [showBulkProductModal, setShowBulkProductModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  // Feb 2026 duplicate-gate — populated when the backend returns 409 on
  // create. Shows a confirm-dialog with the clashing rows; on confirmation
  // we re-POST with ?force=true so the create-anyway is stamped in the audit.
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [duplicateAckReason, setDuplicateAckReason] = useState('');
  // Filter toggle — when on, fetch only products that share their SKU / code
  // / name with at least one other product (server-side computation).
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  // Feb 2026 — SE product-request tickets (Q1(c) — approve auto-creates a
  // pending Product for NSM+ to polish and VP to publish).
  const canTriageRequests = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showRequestsPanel, setShowRequestsPanel] = useState(false);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [newRequest, setNewRequest] = useState({ name: '', category: CATEGORIES[0], expected_price: '', expected_use: '', notes: '' });
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useViewMode('products');
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'ICU Care',
    hsn_code: '',
    product_code: '',
    sku_code: '',
    tax_bracket: 0,
    video_link: '',
    cogs_price: ''
  });
  const [productImages, setProductImages] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadType, setUploadType] = useState('image');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicatesOnly]);

  useEffect(() => {
    // Load pending request count for the header badge — cheap and useful.
    loadRequests({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRequests = async ({ silent } = {}) => {
    // Only flip to the Loading… placeholder if we have nothing to show yet.
    // Otherwise the user sees the (slightly stale) list instantly and it
    // silently refreshes in the background.
    const showLoading = !silent && requests.length === 0;
    if (showLoading) setRequestsLoading(true);
    try {
      const res = await axios.get(`${API}/product-requests`);
      setRequests(res.data);
      setPendingRequestCount(res.data.filter((r) => r.status === 'pending').length);
    } catch (e) {
      if (!silent) console.error('Error loading product requests', e);
    } finally {
      if (showLoading) setRequestsLoading(false);
    }
  };

  const submitProductRequest = async (e) => {
    e.preventDefault?.();
    if (!newRequest.name.trim() || !newRequest.category.trim()) return;
    setRequestSubmitting(true);
    try {
      await axios.post(`${API}/product-requests`, {
        ...newRequest,
        expected_price: parseFloat(newRequest.expected_price) || 0,
      });
      setShowRequestModal(false);
      setNewRequest({ name: '', category: CATEGORIES[0], expected_price: '', expected_use: '', notes: '' });
      await loadRequests({ silent: true });
      notify('Product request submitted. NSM will review it shortly.');
    } catch (err) {
      notify(err?.response?.data?.detail || 'Failed to submit request');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const approveRequest = async (req) => {
    if (!await confirm({ message: `Approve "${req.name}"? A draft product will be created for you to add media and finalise pricing.` })) return;
    try {
      const res = await axios.post(`${API}/product-requests/${req.id}/approve`, { product: {} });
      await loadRequests({ silent: true });
      await fetchProducts();
      const goEdit = await confirm({ message: `Draft product created. Open it now to add media and finalise?` });
      if (goEdit) navigate(`/products/${res.data.resulting_product_id}`);
    } catch (err) {
      notify(err?.response?.data?.detail || 'Failed to approve');
    }
  };

  const rejectRequest = async (req) => {
    const reason = window.prompt(`Reject "${req.name}"?\n\nEnter a short reason (visible to the requester):`);
    if (reason === null || !reason.trim()) return;
    try {
      await axios.post(`${API}/product-requests/${req.id}/reject`, { reason });
      await loadRequests({ silent: true });
    } catch (err) {
      notify(err?.response?.data?.detail || 'Failed to reject');
    }
  };

  const fetchProducts = async () => {
    try {
      const params = duplicatesOnly ? '?duplicates_only=true' : '';
      const response = await axios.get(`${API}/products${params}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e, opts = {}) => {
    e.preventDefault?.();

    if (!productImages.length) {
      notify('Please upload at least one product image');
      return;
    }

    try {
      // First create the product
      const canEditCogs = ['Vice President', 'President'].includes(user?.role);
      const productPayload = {
        ...formData,
        price: parseFloat(formData.price),
        tax_bracket: parseFloat(formData.tax_bracket)
      };
      if (canEditCogs) {
        productPayload.cogs_price = formData.cogs_price === '' ? 0 : parseFloat(formData.cogs_price);
      } else {
        delete productPayload.cogs_price;
      }
      // Feb 2026 duplicate-gate — first attempt goes with no `force` query param
      // (backend defaults to false). Retry after acknowledgement adds
      // ?force=true&ack_reason=... so the record is stamped.
      const params = new URLSearchParams();
      if (opts.force) params.set('force', 'true');
      if (opts.ackReason) params.set('ack_reason', opts.ackReason);
      const url = params.toString() ? `${API}/products?${params.toString()}` : `${API}/products`;
      const response = await axios.post(url, productPayload);

      const productId = response.data.id;

      // Then upload every selected image in parallel — first one in the list
      // becomes the primary image because backend stores in insertion order.
      const uploads = productImages.map((f) => {
        const fd = new FormData();
        fd.append('file', f);
        return axios.post(`${API}/products/${productId}/upload?file_type=image`, fd);
      });
      const results = await Promise.allSettled(uploads);
      const failed = results.filter((r) => r.status === 'rejected').length;

      fetchProducts();
      handleCloseModal();
      setDuplicateWarning(null);
      if (failed > 0) {
        notify(`Product created but ${failed} of ${productImages.length} image(s) failed to upload. You can add them again from the product's Upload button.`);
      } else {
        notify(`Product created with ${productImages.length} image(s)! It will be visible after approval from National Sales Manager, VP, or President.`);
      }
    } catch (error) {
      // Backend flags duplicates with HTTP 409 + { duplicates: [...] }. We
      // surface those in an inline dialog rather than a bare alert so the
      // creator sees exactly what already exists before deciding to proceed.
      // NOTE: we CLOSE the Add-Product modal before opening the warning so
      // the warning isn't hidden behind Radix's Dialog portal (iter60 bug).
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.code === 'duplicate_product') {
        setShowModal(false);
        setDuplicateWarning({ duplicates: detail.duplicates || [], message: detail.message });
        return;
      }
      console.error('Error creating product:', error);
      notify(typeof detail === 'string' ? detail : (detail?.message || 'Error creating product'));
    }
  };

  const handleHsnChange = (hsnCode) => {
    const taxRate = getTaxBracket(hsnCode);
    setFormData({
      ...formData,
      hsn_code: hsnCode,
      tax_bracket: taxRate
    });
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFiles.length || !selectedProduct) return;

    // Fire all uploads in parallel and report per-file success/failure.
    const uploads = uploadFiles.map((f) => {
      const fd = new FormData();
      fd.append('file', f);
      return axios.post(`${API}/products/${selectedProduct.id}/upload?file_type=${uploadType}`, fd);
    });
    const results = await Promise.allSettled(uploads);
    const failed = results.filter((r) => r.status === 'rejected').length;

    fetchProducts();
    setShowUploadModal(false);
    setUploadFiles([]);
    if (failed > 0) {
      notify(`${uploadFiles.length - failed} of ${uploadFiles.length} file(s) uploaded. ${failed} failed — please check formats and try again.`);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete product?',
      message: 'Are you sure you want to delete this product?',
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'ICU Care',
      hsn_code: '',
      product_code: '',
      sku_code: '',
      tax_bracket: 0,
      video_link: '',
      cogs_price: ''
    });
    setProductImages([]);
  };

  const openUploadModal = (product) => {
    setSelectedProduct(product);
    setShowUploadModal(true);
  };

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem('token');
    if (!token) { notify('Please log in again to download the template.'); return; }
    const link = document.createElement('a');
    link.href = `${API}/products-export/csv?token=${encodeURIComponent(token)}`;
    link.setAttribute('download', 'product_prices.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const toggleCompareSelect = (product) => {
    setSelectedForCompare(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) return prev.filter(p => p.id !== product.id);
      if (prev.length >= 3) { notify('You can compare up to 3 products'); return prev; }
      if (prev.length > 0 && prev[0].category !== product.category) {
        notify(`You can only compare products in the same category.\nSelected: ${prev[0].category}\nThis product: ${product.category}`);
        return prev;
      }
      return [...prev, product];
    });
  };

  const handleCompare = () => {
    if (selectedForCompare.length < 2) { notify('Select at least 2 products to compare'); return; }
    const ids = selectedForCompare.map(p => p.id).join(',');
    navigate(`/products/compare?ids=${ids}`);
  };

  const isSelectedForCompare = (productId) => selectedForCompare.some(p => p.id === productId);
  const compareCategory = selectedForCompare.length > 0 ? selectedForCompare[0].category : null;

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const resp = await axios.post(`${API}/products-import/bulk-price`, formData);
      setBulkResult(resp.data);
      if (resp.data.updated > 0) fetchProducts();
    } catch (error) {
      console.error('Error bulk uploading:', error);
      setBulkResult({ updated: 0, errors: [error.response?.data?.detail || 'Upload failed'] });
    } finally {
      setBulkUploading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p => {
      const hay = [
        p.name, p.category, p.product_code, p.sku_code, p.hsn_code,
        p.description, p.status,
      ].filter(Boolean).join(' | ').toLowerCase();
      return hay.includes(q);
    });
  }, [products, searchTerm]);

  const visibleIds = useMemo(() => filteredProducts.map(p => p.id), [filteredProducts]);
  const selection = useBulkSelection(visibleIds);
  const canBulkDelete = ['Vice President', 'President', 'Sales Coordinator'].includes(user?.role);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const submitBulkDelete = async ({ reason }) => {
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API}/products/bulk-delete`, {
        product_ids: selection.selectedIds,
        reason,
      });
      notify(`✅ ${r.data.deleted} product(s) deleted.`);
      setShowBulkDeleteDialog(false);
      selection.clear();
      await fetchProducts();
    } catch (e) {
      notify(e.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkDeleting(false); }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="products-title">
            Product Catalogue
          </h1>
          <p className="text-[#6D8E87]">Manage your hospital furniture products</p>
        </div>
        <div className="flex gap-3">
          {canBulkUpload && (
            <button onClick={() => setShowBulkProductModal(true)}
              className="flex items-center gap-2 px-5 py-3 border-2 border-[#00a69c] text-[#00a69c] rounded-full font-medium hover:bg-[#E2F3F0] transition"
              data-testid="bulk-products-button">
              <Upload size={20} weight="bold" /> Bulk Upload
            </button>
          )}
          {isVpPlus && (
            <button
              onClick={() => { setShowBulkModal(true); setBulkResult(null); setBulkFile(null); }}
              className="flex items-center gap-2 px-5 py-3 border-2 border-[#00a69c] text-[#00a69c] rounded-full font-medium hover:bg-[#E2F3F0] transition"
              data-testid="bulk-price-button"
            >
              <Table size={20} weight="bold" />
              Bulk Price Update
            </button>
          )}
          {canCreateProduct && (
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-product-button"
          >
            <Plus size={20} weight="bold" />
            Add Product
          </button>
          )}
          {!canCreateProduct && (
            <button
              onClick={() => setShowRequestModal(true)}
              className="flex items-center gap-2 px-5 py-3 rounded-full font-medium bg-[#00a69c] text-white hover:bg-[#008a82] transition"
              data-testid="request-product-button"
            >
              <Plus size={20} weight="bold" />
              Request New Product
            </button>
          )}
          <button
            onClick={() => { setShowRequestsPanel(true); loadRequests(); }}
            className="flex items-center gap-2 px-5 py-3 border-2 border-[#00a69c] text-[#00a69c] rounded-full font-medium hover:bg-[#E2F3F0] transition relative"
            data-testid="view-requests-button"
          >
            <Tray size={20} weight="bold" />
            Requests
            {pendingRequestCount > 0 && (
              <span className="ml-1 bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="pending-requests-badge">
                {pendingRequestCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#6D8E87]" />
          <input
            type="text"
            placeholder="Search by name, category, product code, SKU or HSN…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
            data-testid="search-products-input"
          />
        </div>
      </div>

      {/* Feb 2026 — Duplicates-only filter. Toggle chip so VP+ can quickly
          audit the catalogue and clean up. Anyone with product-read access
          can view; only VP+ can actually delete via the existing per-card
          Delete button, which already stays behind that gate. */}
      <div className="flex items-center gap-2 mb-4" data-testid="products-filter-chips">
        <button
          type="button"
          onClick={() => setDuplicatesOnly(prev => !prev)}
          data-testid="filter-duplicates-only"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
            duplicatesOnly
              ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
              : 'bg-white text-rose-700 border-rose-200 hover:border-rose-500'
          }`}
        >
          {duplicatesOnly ? '● ' : ''}Show duplicates only
        </button>
        {duplicatesOnly && (
          <span className="text-[11px] text-[#6D8E87]" data-testid="duplicates-count">
            {filteredProducts.length} duplicate row{filteredProducts.length === 1 ? '' : 's'}
          </span>
        )}
        <div className="ml-auto">
          <ViewToggle mode={viewMode} onChange={setViewMode} testIdPrefix="products-view" />
        </div>
      </div>

      {/* Bulk-select header (VP+) */}
      {canBulkDelete && filteredProducts.length > 0 && (
        <div className="flex items-center gap-2 mb-3 text-sm text-[#3B5A54]" data-testid="products-bulk-select-header">
          <button
            type="button"
            onClick={selection.toggleAll}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-[#E2F3F0] transition"
            data-testid="products-bulk-select-all-toggle"
          >
            {selection.isAllSelected
              ? <CheckSquare size={20} weight="fill" className="text-red-600" />
              : <Square size={20} className={selection.isSomeSelected ? 'text-red-600' : 'text-[#6D8E87]'} />}
            <span className="font-medium">
              {selection.isAllSelected ? 'Deselect all' : `Select all ${filteredProducts.length}`}
            </span>
          </button>
          {selection.count > 0 && <span className="text-xs text-[#6D8E87]">· {selection.count} selected</span>}
        </div>
      )}

      {/* Products Grid (Tetris Layout) */}
      {viewMode === 'cards' && (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 pb-24" data-testid="products-grid">
        {filteredProducts.map((product, index) => (
          <div
            key={product.id}
            className={`medical-card overflow-hidden card-hover relative ${isSelectedForCompare(product.id) ? 'ring-2 ring-[#00a69c]' : ''} ${selection.isSelected(product.id) ? 'ring-2 ring-red-500 shadow-lg' : ''}`}
            data-testid={`product-card-${product.id}`}
          >
            {/* Bulk-select checkbox (top-right, VP+ only) */}
            {canBulkDelete && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); selection.toggle(product.id); }}
                className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-white/90 border border-[#D5E8E4] flex items-center justify-center shadow hover:shadow-md transition"
                data-testid={`product-bulk-checkbox-${product.id}`}
                title="Select for bulk delete"
              >
                {selection.isSelected(product.id)
                  ? <CheckSquare size={16} weight="fill" className="text-red-600" />
                  : <Square size={16} className="text-[#6D8E87]" />}
              </button>
            )}
            {/* Compare checkbox */}
            <button
              onClick={() => toggleCompareSelect(product)}
              className={`absolute top-3 left-3 z-10 w-7 h-7 rounded-full border-2 flex items-center justify-center transition ${
                isSelectedForCompare(product.id) 
                  ? 'bg-[#00a69c] border-[#00a69c] text-white' 
                  : compareCategory && compareCategory !== product.category
                    ? 'border-[#D5E8E4] bg-white/70 opacity-40 cursor-not-allowed'
                    : 'border-[#D5E8E4] bg-white/80 hover:border-[#00a69c]'
              }`}
              disabled={compareCategory && compareCategory !== product.category && !isSelectedForCompare(product.id)}
              data-testid={`compare-check-${product.id}`}
              title={compareCategory && compareCategory !== product.category ? `Only ${compareCategory} products can be compared` : 'Select to compare'}
            >
              {isSelectedForCompare(product.id) && <Scales size={14} weight="bold" />}
            </button>

            {/* Product Image + content area — clickable link to detail page */}
            <Link
              to={`/products/${product.id}`}
              className="block hover:opacity-95"
              data-testid={`product-link-${product.id}`}
            >
              <div className="h-48 bg-[#E2F3F0] flex items-center justify-center relative">
                {product.images.length > 0 ? (
                  <img
                    src={fileUrl(product.images[0])}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src="https://images.unsplash.com/photo-1641877492086-771bc14a8e5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxob3NwaXRhbCUyMGZ1cm5pdHVyZXxlbnwwfHx8fDE3NzU3MTczNTl8MA&ixlib=rb-4.1.0&q=85"
                    alt={product.name}
                    className="w-full h-full object-cover opacity-50"
                  />
                )}
              </div>

              <div className="px-4 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
                    {product.product_code || product.name}
                  </h3>
                  {product.status === 'pending' && (
                    <span className="px-2 py-1 bg-[#FEF3C7] text-[#92400E] text-xs rounded-full">
                      PENDING
                    </span>
                  )}
                  {product.status === 'approved' && (
                    <span className="px-2 py-1 bg-[#D1FAE5] text-[#065F46] text-xs rounded-full">
                      APPROVED
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#6D8E87] mb-2">{product.category}</p>
                <p className="text-sm text-[#3B5A54] mb-3 line-clamp-2">{product.name}</p>
                {product.sku_code && (
                  <div className="flex gap-3 mb-2 text-xs">
                    <span className="text-[#3B5A54]">SKU: <span className="font-semibold">{product.sku_code}</span></span>
                  </div>
                )}
                <div className="text-2xl font-bold text-[#00a69c] mb-4" style={{ fontFamily: 'Outfit' }}>
                  ₹{product.price.toLocaleString('en-IN')}
                </div>
              </div>
            </Link>

            <div className="p-4 pt-0">
              {product.video_link && (
                <a
                  href={product.video_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mb-2 text-xs text-[#00a69c] hover:underline"
                  data-testid={`video-link-${product.id}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0C.488 3.45.029 5.804 0 12c.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0C23.512 20.55 23.971 18.196 24 12c-.029-6.185-.484-8.549-4.385-8.816zM9 16V8l8 4-8 4z"/></svg>
                  Watch Video
                </a>
              )}

              <div className="flex gap-2">
                {canCreateProduct && (
                <button
                  onClick={() => openUploadModal(product)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#E2F3F0] text-[#00a69c] rounded-lg hover:bg-[#B5D9D2] transition text-sm"
                  data-testid={`upload-${product.id}`}
                >
                  <Upload size={16} />
                  Upload
                </button>
                )}
                {isVpPlus && (
                <button
                  onClick={() => handleDelete(product.id)}
                  className="w-10 h-10 flex items-center justify-center text-[#EF4444] hover:bg-red-50 rounded-lg transition"
                  data-testid={`delete-product-${product.id}`}
                >
                  <Trash size={18} />
                </button>
                )}
              </div>

              {/* File Indicators */}
              <div className="flex gap-2 mt-3 text-xs">
                {product.images.length > 0 && (
                  <span className="px-2 py-1 bg-[#E2F3F0] text-[#00a69c] rounded">
                    {product.images.length} images
                  </span>
                )}
                {product.videos.length > 0 && (
                  <span className="px-2 py-1 bg-[#DBEAFE] text-[#3B82F6] rounded">
                    {product.videos.length} videos
                  </span>
                )}
                {product.catalogues.length > 0 && (
                  <span className="px-2 py-1 bg-[#FEF3C7] text-[#92400E] rounded">
                    {product.catalogues.length} PDFs
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {viewMode === 'list' && (
        <div className="pb-24">
          <PowerTable
            testIdPrefix="products-table"
            initialSort={{ key: 'updated_at', dir: 'desc' }}
            selection={canBulkDelete ? selection : undefined}
            onRowClick={(row) => navigate(`/products/${row.id}`)}
            rows={filteredProducts}
            columns={[
              { key: 'name', label: 'Product', sortable: true, cell: (r) => (
                <div>
                  <div className="font-semibold text-[#052A23]">{r.name}</div>
                  <div className="text-[11px] text-[#6D8E87]">{r.product_code || r.sku_code || '—'}</div>
                </div>
              ) },
              { key: 'category', label: 'Category', sortable: true },
              { key: 'price', label: 'Price', sortable: true, className: 'text-right',
                accessor: (r) => Number(r.price || 0),
                cell: (r) => <span className="font-semibold text-[#00a69c]">₹{Number(r.price || 0).toLocaleString('en-IN')}</span> },
              { key: 'hsn_code', label: 'HSN', sortable: true, cell: (r) => r.hsn_code || '—' },
              { key: 'status', label: 'Status', sortable: true, cell: (r) => (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  r.status === 'approved' ? 'bg-emerald-100 text-emerald-700'
                  : r.status === 'rejected' ? 'bg-rose-100 text-rose-700'
                  : 'bg-amber-100 text-amber-700'
                }`}>{(r.status || '').toUpperCase()}</span>
              ) },
              { key: 'created_by_name', label: 'Created By', sortable: true, cell: (r) => r.created_by_name || '—' },
            ]}
          />
        </div>
      )}
      {selectedForCompare.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#052A23] text-white px-6 py-3.5 rounded-2xl shadow-2xl flex items-center gap-4 animate-in slide-in-from-bottom" data-testid="compare-bar">
          <Scales size={20} className="text-[#40C9A2]" weight="bold" />
          <span className="text-sm font-medium">
            {selectedForCompare.length} product{selectedForCompare.length > 1 ? 's' : ''} selected
            <span className="text-[#40C9A2] ml-1">({selectedForCompare[0].category})</span>
          </span>
          <button onClick={() => setSelectedForCompare([])}
            className="px-3 py-1.5 text-xs bg-white/10 rounded-lg hover:bg-white/20 transition"
            data-testid="compare-clear-btn">
            Clear
          </button>
          <button onClick={handleCompare} disabled={selectedForCompare.length < 2}
            className="px-5 py-1.5 bg-[#00a69c] text-white text-sm font-semibold rounded-lg hover:bg-[#00bfb3] transition disabled:opacity-40"
            data-testid="compare-go-btn">
            Compare {selectedForCompare.length >= 2 ? `(${selectedForCompare.length})` : '— select at least 2'}
          </button>
        </div>
      )}

      {/* Add Product Modal */}
      {showModal && (
        <Dialog open={showModal} onOpenChange={handleCloseModal}>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="product-modal">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                Add New Product
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Product Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    data-testid="product-category-input"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">HSN Code *</label>
                  <input
                    type="text"
                    value={formData.hsn_code}
                    onChange={(e) => handleHsnChange(e.target.value)}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    placeholder="e.g., 9018"
                    required
                    data-testid="hsn-code-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Product Code *</label>
                  <input
                    type="text"
                    value={formData.product_code}
                    onChange={(e) => setFormData({ ...formData, product_code: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    placeholder="e.g., PC001"
                    required
                    data-testid="product-code-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">SKU Code <span className="text-xs text-[#6D8E87]">(optional)</span></label>
                  <input
                    type="text"
                    value={formData.sku_code}
                    onChange={(e) => setFormData({ ...formData, sku_code: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    placeholder="e.g., SKU001 (leave blank if not applicable)"
                    data-testid="sku-code-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Price (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    data-testid="product-price-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Tax Bracket (GST %) - Auto-filled</label>
                  <input
                    type="number"
                    value={formData.tax_bracket}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg bg-[#F4F9F7] outline-none"
                    readOnly
                    data-testid="tax-bracket-input"
                  />
                  <p className="text-xs text-[#6D8E87] mt-1">Automatically calculated based on HSN code</p>
                </div>
              </div>

              {['Vice President', 'President'].includes(user?.role) && (
                <div className="p-3 rounded-lg border border-[#F59E0B]/30 bg-[#FFFBEB]" data-testid="cogs-create-panel">
                  <label className="block text-xs font-semibold text-[#92400E] mb-1 uppercase tracking-wide">Cost of Goods Sold (COGS) — Executive Only</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={formData.cogs_price}
                    onChange={(e) => setFormData({ ...formData, cogs_price: e.target.value })}
                    placeholder="e.g. 45000"
                    className="w-full px-4 py-2 border border-[#F59E0B]/40 rounded-md text-sm focus:ring-2 focus:ring-[#F59E0B]/30 focus:border-[#F59E0B] bg-white outline-none"
                    data-testid="cogs-price-input"
                  />
                  <p className="text-[11px] text-[#92400E] mt-1">Feeds Gross Margin analytics. Never shown to Sales Executives / RSM / NSM.</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-2">Product Images * (Select one or more — first image becomes primary)</label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => setProductImages(Array.from(e.target.files || []))}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  accept="image/*"
                  required
                  data-testid="product-image-input"
                />
                <p className="text-xs text-[#6D8E87] mt-1">
                  Hold Ctrl / Cmd to select multiple JPEG/PNG/WEBP images.
                  {productImages.length > 0 && (
                    <span className="ml-1 font-semibold text-[#00a69c]" data-testid="product-image-count">
                      {productImages.length} file{productImages.length === 1 ? '' : 's'} selected
                    </span>
                  )}
                </p>
                {productImages.length > 0 && (
                  <ul className="mt-2 space-y-1" data-testid="product-image-preview-list">
                    {productImages.map((f, i) => (
                      <li key={i} className="text-xs text-[#3B5A54] flex items-center justify-between bg-[#F4F9F7] rounded px-2 py-1">
                        <span className="truncate flex-1 mr-2">
                          {i === 0 && <span className="text-[10px] mr-1 px-1.5 py-0.5 bg-[#00a69c] text-white rounded font-bold uppercase tracking-wider">Primary</span>}
                          {f.name}
                          <span className="text-[#6D8E87] ml-1">({(f.size / 1024).toFixed(0)} KB)</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setProductImages(productImages.filter((_, idx) => idx !== i))}
                          className="text-red-500 hover:text-red-700 text-xs"
                          data-testid={`remove-image-${i}`}
                          title="Remove"
                        >×</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-2">YouTube Video Link (Optional)</label>
                <input
                  type="url"
                  value={formData.video_link}
                  onChange={(e) => setFormData({ ...formData, video_link: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  placeholder="https://www.youtube.com/watch?v=..."
                  data-testid="video-link-input"
                />
                <p className="text-xs text-[#6D8E87] mt-1">YouTube link will be included in the quotation PDF for customers</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  rows="3"
                  required
                  data-testid="product-description-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={handleCloseModal} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1" data-testid="save-product-button">
                  Create Product
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Upload File Modal */}
      {showUploadModal && (
        <Dialog open={showUploadModal} onOpenChange={() => setShowUploadModal(false)}>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="upload-modal">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                Upload File
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleFileUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-2">File Type</label>
                <select
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value)}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  data-testid="upload-type-select"
                >
                  <option value="image">Image (JPEG/PNG)</option>
                  <option value="video">Video (MP4)</option>
                  <option value="catalogue">Catalogue (PDF)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-2">
                  Select File{uploadType === 'catalogue' ? '' : 's'}
                </label>
                <input
                  type="file"
                  multiple={uploadType !== 'catalogue'}
                  onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  accept={uploadType === 'image' ? 'image/*' : uploadType === 'video' ? 'video/*' : '.pdf'}
                  required
                  data-testid="file-input"
                />
                <p className="text-xs text-[#6D8E87] mt-1">
                  {uploadType === 'catalogue'
                    ? 'PDFs upload one at a time.'
                    : 'Hold Ctrl / Cmd to select multiple files.'}
                  {uploadFiles.length > 0 && (
                    <span className="ml-1 font-semibold text-[#00a69c]" data-testid="upload-file-count">
                      {uploadFiles.length} file{uploadFiles.length === 1 ? '' : 's'} selected
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1" data-testid="upload-file-button">
                  Upload
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Price Update Modal */}
      {showBulkModal && (
        <Dialog open={showBulkModal} onOpenChange={() => setShowBulkModal(false)}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="bulk-price-modal">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
                Bulk Price Update
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 mt-2">
              {/* Step 1 */}
              <div className="p-4 bg-[#F4F9F7] rounded-xl border border-[#D5E8E4]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#00a69c] text-white flex items-center justify-center text-sm font-bold">1</div>
                  <h4 className="text-sm font-semibold text-[#052A23]">Download Price Template</h4>
                </div>
                <p className="text-xs text-[#6D8E87] mb-3 ml-10">CSV file with all products, current prices, and an empty "new_price" column for you to fill.</p>
                <button onClick={handleDownloadTemplate}
                  className="ml-10 flex items-center gap-2 px-4 py-2 bg-white border border-[#D5E8E4] rounded-lg text-sm font-medium text-[#00a69c] hover:bg-[#E2F3F0] transition"
                  data-testid="download-template-btn">
                  <DownloadSimple size={16} weight="bold" /> Download CSV Template
                </button>
              </div>

              {/* Step 2 */}
              <div className="p-4 bg-[#F4F9F7] rounded-xl border border-[#D5E8E4]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#00a69c] text-white flex items-center justify-center text-sm font-bold">2</div>
                  <h4 className="text-sm font-semibold text-[#052A23]">Fill in New Prices</h4>
                </div>
                <p className="text-xs text-[#6D8E87] ml-10">Open in Excel/Google Sheets. Fill the <strong>new_price</strong> column for products you want to update. Leave blank to skip.</p>
              </div>

              {/* Step 3 */}
              <div className="p-4 bg-[#F4F9F7] rounded-xl border border-[#D5E8E4]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#00a69c] text-white flex items-center justify-center text-sm font-bold">3</div>
                  <h4 className="text-sm font-semibold text-[#052A23]">Upload Updated CSV</h4>
                </div>
                <div className="ml-10">
                  <input type="file" accept=".csv" onChange={(e) => { setBulkFile(e.target.files[0]); setBulkResult(null); }}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
                    data-testid="bulk-csv-input" />
                </div>
              </div>

              {/* Result */}
              {bulkResult && (
                <div className={`p-4 rounded-xl border ${bulkResult.updated > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="bulk-result">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-sm font-bold ${bulkResult.updated > 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {bulkResult.updated} product(s) updated
                    </span>
                    {bulkResult.skipped > 0 && <span className="text-xs text-[#6D8E87]">{bulkResult.skipped} skipped (empty)</span>}
                  </div>
                  {bulkResult.errors?.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      {bulkResult.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-600">{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowBulkModal(false)} className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50 transition">Close</button>
                <button onClick={handleBulkUpload} disabled={!bulkFile || bulkUploading}
                  className="flex-1 py-3 bg-[#00a69c] text-white rounded-lg font-semibold hover:bg-[#00bfb3] transition disabled:opacity-40"
                  data-testid="bulk-upload-btn">
                  {bulkUploading ? 'Updating...' : 'Update Prices'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      <BulkUploadModal open={showBulkProductModal} onClose={() => setShowBulkProductModal(false)}
        title="Bulk Upload Products" exportUrl="/products-export/full-csv" importUrl="/products-import/bulk"
        templateName="products_full_template.csv" onSuccess={fetchProducts}
        instructions={`CSV with columns: name, description, price, category, hsn_code, product_code, sku_code, tax_bracket. Name is required. ${isVpPlus ? 'Existing product_codes will be updated.' : 'Rows whose product_code already exists are skipped (only VP/President can update existing products).'}`} />

      {/* Feb 2026 duplicate-gate — surfaced when POST /products returns 409. */}
      {duplicateWarning && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDuplicateWarning(null)}
          data-testid="duplicate-warning-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-amber-200 bg-amber-50">
              <h3 className="text-lg font-bold text-amber-900 flex items-center gap-2">
                ⚠ Similar product already exists
              </h3>
              <p className="text-sm text-amber-800 mt-1">
                {duplicateWarning.duplicates.length} product{duplicateWarning.duplicates.length === 1 ? '' : 's'} share
                the same SKU / product code / name. Please review before creating.
              </p>
            </div>
            <div className="p-5 space-y-3">
              {duplicateWarning.duplicates.map((d) => (
                <div key={d.id} className="p-3 border border-[#D5E8E4] rounded bg-[#F9FCFB]" data-testid={`duplicate-row-${d.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-[#052A23]">{d.name}</div>
                      <div className="text-xs text-[#6D8E87] mt-0.5">
                        {d.product_code && <>Code: <b>{d.product_code}</b> · </>}
                        {d.sku_code && <>SKU: <b>{d.sku_code}</b> · </>}
                        Price: <b>₹{Number(d.price || 0).toLocaleString('en-IN')}</b>
                      </div>
                      <div className="text-[11px] text-[#6D8E87] mt-1">
                        Created by {d.created_by_name || 'unknown'} · Status: {d.status}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <div className="mt-4">
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">
                  If you still want to create this product, tell us why (optional but recommended — this is recorded in the audit log)
                </label>
                <textarea
                  value={duplicateAckReason}
                  onChange={(e) => setDuplicateAckReason(e.target.value)}
                  placeholder="e.g. Different supplier / newer revision / regional pricing…"
                  rows={2}
                  className="w-full px-3 py-2 border border-[#D5E8E4] rounded text-sm focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  data-testid="duplicate-ack-reason-input"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t bg-slate-50 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setDuplicateWarning(null); setDuplicateAckReason(''); setShowModal(true); }}
                className="px-4 py-2 text-sm border border-[#D5E8E4] rounded hover:bg-white text-[#3B5A54]"
                data-testid="duplicate-cancel-btn"
              >
                Cancel — edit form
              </button>
              <button
                type="button"
                onClick={() => handleSubmit({ preventDefault: () => {} }, { force: true, ackReason: duplicateAckReason })}
                className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded font-medium"
                data-testid="duplicate-confirm-btn"
              >
                I understand — create anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <BulkActionBar count={selection.count} onClear={selection.clear} testid="products-bulk-action-bar">
        {canBulkDelete && (
          <ScGatedButton
            action="bulk-delete"
            type="button"
            onClick={() => setShowBulkDeleteDialog(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-red-600 hover:bg-red-700 rounded-full transition"
            data-testid="products-bulk-delete-btn"
          >
            <Trash size={14} weight="bold" /> Delete
          </ScGatedButton>
        )}
      </BulkActionBar>
      <BulkDeleteDialog
        open={showBulkDeleteDialog}
        onClose={() => setShowBulkDeleteDialog(false)}
        onConfirm={submitBulkDelete}
        entityLabel="product" entityLabelPlural="products"
        count={selection.count}
        loading={bulkDeleting}
      />

      {/* --- Feb 2026: SE Request-New-Product modal --- */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="max-w-lg" data-testid="request-product-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Request New Product</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitProductRequest} className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Product Name *</label>
              <input
                type="text" required value={newRequest.name}
                onChange={(e) => setNewRequest({ ...newRequest, name: e.target.value })}
                className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm focus:outline-none focus:border-[#00a69c]"
                data-testid="request-name-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Category *</label>
              <select
                required value={newRequest.category}
                onChange={(e) => setNewRequest({ ...newRequest, category: e.target.value })}
                className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm bg-white focus:outline-none focus:border-[#00a69c]"
                data-testid="request-category-select"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Expected Price (₹)</label>
              <input
                type="number" min="0" step="1000" value={newRequest.expected_price}
                onChange={(e) => setNewRequest({ ...newRequest, expected_price: e.target.value })}
                className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm focus:outline-none focus:border-[#00a69c]"
                data-testid="request-price-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Target Customer / Use Case</label>
              <textarea
                rows={2} value={newRequest.expected_use}
                onChange={(e) => setNewRequest({ ...newRequest, expected_use: e.target.value })}
                className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm focus:outline-none focus:border-[#00a69c]"
                placeholder="e.g., Fortis Gurgaon ICU expansion, competitor is Meditek TC-500"
                data-testid="request-use-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Notes for NSM</label>
              <textarea
                rows={2} value={newRequest.notes}
                onChange={(e) => setNewRequest({ ...newRequest, notes: e.target.value })}
                className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm focus:outline-none focus:border-[#00a69c]"
                data-testid="request-notes-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowRequestModal(false)} className="flex-1 px-4 py-2 border border-[#B8D5CE] text-[#3B5A54] rounded-lg text-sm font-medium">Cancel</button>
              <button
                type="submit" disabled={requestSubmitting || !newRequest.name.trim()}
                className="flex-1 btn-primary text-sm disabled:opacity-60"
                data-testid="request-submit-button"
              >
                {requestSubmitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* --- Feb 2026: Product Requests panel (triage for NSM+; personal history for SE) --- */}
      <Dialog open={showRequestsPanel} onOpenChange={setShowRequestsPanel}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="requests-panel-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>
              {canTriageRequests ? 'Product Requests · Triage' : 'My Product Requests'}
            </DialogTitle>
          </DialogHeader>
          {requestsLoading ? (
            <InlineSkeleton />
          ) : requests.length === 0 ? (
            <p className="text-sm text-[#6D8E87] py-6 text-center" data-testid="requests-empty">
              {canTriageRequests ? 'No product requests yet.' : "You haven't raised any requests yet."}
            </p>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => (
                <div key={r.id} className="border border-[#E2ECEA] rounded-lg p-3" data-testid={`request-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-[#052A23] truncate">{r.name}</h4>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            r.status === 'pending' ? 'bg-amber-100 text-amber-700'
                            : r.status === 'approved' ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-rose-100 text-rose-700'
                          }`}
                          data-testid={`request-status-${r.id}`}
                        >
                          {r.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-xs text-[#6D8E87] mt-0.5">
                        {r.category}
                        {r.expected_price ? ` · Expected ₹${Number(r.expected_price).toLocaleString('en-IN')}` : ''}
                        {' · by '}{r.requested_by_name}
                      </p>
                      {r.expected_use && <p className="text-xs text-[#3B5A54] mt-1"><span className="font-medium">Use:</span> {r.expected_use}</p>}
                      {r.notes && <p className="text-xs text-[#3B5A54] mt-0.5"><span className="font-medium">Notes:</span> {r.notes}</p>}
                      {r.status !== 'pending' && (
                        <p className="text-[11px] text-[#6D8E87] mt-1">
                          {r.status === 'approved' ? '✓ Approved' : '✕ Rejected'} by {r.reviewer_name}
                          {r.reviewer_notes ? ` — "${r.reviewer_notes}"` : ''}
                        </p>
                      )}
                      {r.resulting_product_id && (
                        <button
                          onClick={() => { setShowRequestsPanel(false); navigate(`/products/${r.resulting_product_id}`); }}
                          className="text-xs text-[#00a69c] hover:underline mt-1"
                          data-testid={`request-goto-product-${r.id}`}
                        >
                          → Open draft product
                        </button>
                      )}
                    </div>
                    {canTriageRequests && r.status === 'pending' && (
                      <div className="flex flex-col gap-1 shrink-0">
                        <button
                          onClick={() => approveRequest(r)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
                          data-testid={`approve-request-${r.id}`}
                        >
                          <CheckCircle size={14} weight="bold" /> Approve
                        </button>
                        <button
                          onClick={() => rejectRequest(r)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border border-rose-500 text-rose-600 hover:bg-rose-50"
                          data-testid={`reject-request-${r.id}`}
                        >
                          <XCircle size={14} weight="bold" /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
